import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { articlesService } from '../services/api';
import { Article } from '../types';
import { useSeoHead } from '../utils/seoHead';
import { stripHtml } from '../utils/blogFormat';
import ArticleHeader from '../components/blog/ArticleHeader';
import ArticleBody from '../components/blog/ArticleBody';
import ArticleComments from '../components/blog/ArticleComments';
import RelatedArticles from '../components/blog/RelatedArticles';
import { TableOfContentsCollapsible, TableOfContentsSidebar } from '../components/blog/TableOfContents';
import { ArticleTags, AuthorBox, HelpfulFeedback, OrnamentDivider } from '../components/blog/ArticleFooterBlocks';
import { BlogErrorState } from '../components/blog/BlogStates';
import BlogIcon from '../components/blog/BlogIcon';
import { useBlogSettings } from '../components/blog/blogSettings';

const SITE_URL = 'https://manehaghighi.com';

/** اسکلت صفحهٔ مقاله؛ ابعاد ثابت تا CLS صفر شود */
const DetailSkeleton: React.FC = () => (
  <div className="mx-auto flex w-full max-w-[960px] flex-col gap-4 pt-2" role="status" aria-label="در حال بارگذاری مقاله">
    <div className="blog-skeleton h-6 w-24 rounded-full" />
    <div className="blog-skeleton h-10 w-[80%]" />
    <div className="blog-skeleton h-10 w-[55%] lg:hidden" />
    <div className="blog-skeleton h-5 w-full" />
    <div className="blog-skeleton h-5 w-[70%]" />
    <div className="blog-skeleton h-4 w-56" />
    <div className="blog-skeleton mt-4 aspect-video w-full rounded-[12px]" />
    <div className="blog-skeleton mt-4 h-4 w-full" />
    <div className="blog-skeleton h-4 w-full" />
    <div className="blog-skeleton h-4 w-[85%]" />
  </div>
);

const NotFoundState: React.FC = () => (
  <div className="mx-auto flex w-full max-w-[520px] flex-col items-center gap-2 rounded-[12px] border border-dashed border-[#e6e4df] bg-[#f4f3f1] px-4 py-10 text-center">
    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#e6e4df]">
      <BlogIcon name="empty-search" width={24} height={24} />
    </span>
    <h1 className="pt-2 text-[16px] font-bold text-[#222]">مقاله‌ای با این آدرس پیدا نشد</h1>
    <p className="pb-3 text-[12px] leading-5 text-[#666]">شاید مقاله حذف شده یا هنوز منتشر نشده باشد.</p>
    <Link to="/articles" className="min-h-[44px] rounded-[8px] bg-[#4a2e68] px-5 py-3 text-[12px] font-semibold text-white hover:bg-[#392352]">
      بازگشت به فهرست مقالات
    </Link>
  </div>
);

const ArticleDetail: React.FC = () => {
  const settings = useBlogSettings();
  const { slug = '' } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const preview = searchParams.get('preview');

  const [article, setArticle] = useState<Article | null>(null);
  const [related, setRelated] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setNotFound(false);
    setRelated([]);
    try {
      const data = await articlesService.getBySlug(slug, preview);
      setArticle(data);
      articlesService
        .getRelated(data.id, 3)
        .then(setRelated)
        .catch(() => setRelated([]));
    } catch (err: any) {
      setArticle(null);
      if (err?.response?.status === 404) setNotFound(true);
      else setError(err?.response?.data?.message || 'ارتباط با سرور برقرار نشد.');
    } finally {
      setLoading(false);
    }
  }, [slug, preview]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    load();
  }, [load, reloadKey]);

  const url = `${SITE_URL}/articles/${slug}`;
  const metaTitle = article ? `${article.metaTitle || article.title} | ${settings.heroTitle}` : settings.heroTitle;
  const description = article ? article.metaDescription || article.excerpt || stripHtml(article.content, 160) : undefined;
  const image = article ? article.ogImage || article.featuredImage || undefined : undefined;

  useSeoHead(
    {
      title: metaTitle,
      description,
      canonical: article?.canonicalUrl || url,
      robots: article?.isPreview ? 'noindex,nofollow' : undefined,
      meta: article
        ? [
            { name: 'keywords', content: article.metaKeywords?.length ? article.metaKeywords.join(', ') : undefined },
            { property: 'og:type', content: article.ogType || 'article' },
            { property: 'og:title', content: article.ogTitle || article.metaTitle || article.title },
            { property: 'og:description', content: article.ogDescription || description },
            { property: 'og:image', content: image },
            { property: 'og:url', content: url },
            { property: 'og:site_name', content: settings.brandName },
            { property: 'og:locale', content: 'fa_IR' },
            { property: 'article:published_time', content: article.publishedAt || undefined },
            { property: 'article:modified_time', content: article.updatedAt },
            { property: 'article:author', content: article.author || 'تحریریه من حقیقی' },
            { property: 'article:section', content: article.category?.name },
            ...(article.tags || []).map((tag) => ({ property: 'article:tag', content: tag })),
            { name: 'twitter:card', content: article.twitterCard || 'summary_large_image' },
            { name: 'twitter:title', content: article.twitterTitle || article.metaTitle || article.title },
            { name: 'twitter:description', content: article.twitterDescription || description },
            { name: 'twitter:image', content: article.twitterImage || image },
          ]
        : [],
      jsonLd: article
        ? [
            {
              '@context': 'https://schema.org',
              '@type': article.schemaType || 'Article',
              headline: article.title,
              description,
              image: image ? [image] : undefined,
              datePublished: article.publishedAt || article.createdAt,
              dateModified: article.updatedAt,
              author: { '@type': 'Person', name: article.author || 'تحریریه من حقیقی', description: article.authorBio || undefined },
              publisher: { '@type': 'Organization', name: settings.brandName, logo: { '@type': 'ImageObject', url: `${SITE_URL}/assets/logo.png` } },
              mainEntityOfPage: { '@type': 'WebPage', '@id': url },
              keywords: article.tags?.join(', ') || undefined,
              articleSection: article.category?.name,
              wordCount: stripHtml(article.content).split(/\s+/).filter(Boolean).length,
              inLanguage: 'fa-IR',
            },
            {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'خانه', item: `${SITE_URL}/` },
                { '@type': 'ListItem', position: 2, name: 'مقالات', item: `${SITE_URL}/articles` },
                ...(article.category
                  ? [{ '@type': 'ListItem', position: 3, name: article.category.name, item: `${SITE_URL}/articles?category=${encodeURIComponent(article.category.slug)}` }]
                  : []),
              ],
            },
          ]
        : null,
    },
    [article?.id, metaTitle, description, settings.brandName],
  );

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 pb-14 pt-[14px] sm:px-8 lg:pb-10 lg:pt-10">
      {loading ? (
        <DetailSkeleton />
      ) : notFound ? (
        <NotFoundState />
      ) : error || !article ? (
        <div className="mx-auto max-w-[640px]">
          <BlogErrorState title="خطا در بارگذاری مقاله" message={error || undefined} onRetry={() => setReloadKey((k) => k + 1)} />
        </div>
      ) : (
        <>
          {article.isPreview && (
            <div className="mb-4 rounded-[8px] border border-[#fde68a] bg-[#fffbeb] px-4 py-2 text-center text-[12px] font-medium text-[#92400e]">
              حالت پیش‌نمایش — این مقاله هنوز منتشر نشده و بازدید آن شمرده نمی‌شود.
            </div>
          )}

          <ArticleHeader article={article} url={url} />

          {/* تصویر شاخص */}
          {article.featuredImage && (
            <figure className="mx-auto flex w-full max-w-[960px] flex-col gap-2 pt-5 lg:pt-10">
              <div className="aspect-video w-full overflow-hidden rounded-[12px] border border-[#e9e8e5] bg-[#efeeeb] p-px shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] lg:shadow-none">
                <img
                  src={article.featuredImage}
                  alt={article.featuredImageAlt || article.title}
                  loading="eager"
                  decoding="async"
                  className="h-full w-full rounded-[11px] object-cover"
                />
              </div>
              {article.featuredImageCaption && (
                <figcaption className="px-1 text-center text-[12px] font-medium leading-5 text-[#5b5f66]">{article.featuredImageCaption}</figcaption>
              )}
            </figure>
          )}

          {/* دو ستون: فهرست چسبان (راست) + متن (چپ) */}
          <div className="mx-auto w-full max-w-[1040px] pt-6 lg:grid lg:grid-cols-12 lg:gap-10 lg:pt-10">
            <aside className="hidden lg:col-span-4 lg:block">
              {/* زیر هدر چسبان وبلاگ (۶۵ پیکسل) */}
              <div className="sticky top-[89px]">
                <TableOfContentsSidebar items={article.toc || []} readingTime={article.readingTime} quoteText={article.quoteText} quoteAuthor={article.quoteAuthor} />
              </div>
            </aside>

            <article className="flex flex-col gap-6 lg:col-span-8 lg:gap-10">
              <div className="mb-1 lg:hidden">
                <TableOfContentsCollapsible items={article.toc || []} />
              </div>

              <ArticleBody article={article} />

              <OrnamentDivider />

              {/* فاصله‌های موبایل بین بلوک‌های پایانی طبق طرح (۳۲/۲۴/۲۸/۲۸) */}
              <div className="mt-2 lg:mt-0">
                <ArticleTags tags={article.tags || []} />
              </div>

              <HelpfulFeedback article={article} />

              <div className="mt-1 lg:mt-0">
                <AuthorBox article={article} />
              </div>

              <div className="mt-1 lg:mt-0">
                <ArticleComments articleId={article.id} allowComments={article.allowComments !== false} initialCount={article.commentCount} />
              </div>
            </article>
          </div>

          <div className="mx-auto w-full max-w-[1040px] pt-8 lg:pt-6">
            <RelatedArticles articles={related} />
          </div>
        </>
      )}
    </div>
  );
};

export default ArticleDetail;
